import React, { useState } from 'react';
import { Cell, Label, Pie, PieChart, Tooltip } from 'recharts';
import styled from 'styled-components';

import { tokens } from 'containers/Intelligence/theme';
import { useIntelligenceScaleContext } from 'containers/Intelligence/theme/IntelligenceScaleContext';
import { scaledPx } from 'containers/Intelligence/theme/scale';
import { formatNumber } from 'utils/formatters';

import useMeasuredWidth from '../useMeasuredWidth';

export interface IDonutSegment {
  label: string;
  value: number;
  /** A `tokens.color` value — the same colour the legend row for this category carries. */
  color: string;
}

/**
 * The ring's outer diameter in px. Layer 1 draws seven of them (94, 108, 118, 140, and 160 / 202 /
 * 237 for Rewards & Loyalty `589:28651`, the Payments coverage ring `589-29064` and Risk &
 * Protection's two `589-29260` § 4 rings), which is why this was a closed union of those values.
 *
 * PLA-8828 widened it to `number`: Customer 360 draws rings at 67 (`3583-10093`), 68 (`3583-8282`)
 * and 210 (`3583-8064`), none of which the union admits. Every literal that type-checked before
 * still does — the geometry below is arithmetic on the diameter, not a lookup keyed by it.
 */
export type DonutChartSize = number;

export interface IDonutChartProps {
  segments: IDonutSegment[];
  /** Pre-formatted headline figure shown inside the ring. */
  centerValue: string;
  centerLabel?: string;
  /**
   * Draws the centre caption out of the ring while it still names the chart. The Customer Base rings
   * are captioned by a headline above and a definition below, so the screens leave the hole bare —
   * but a ring whose accessible name is only "72.1%" says nothing about what was measured.
   */
  centerLabelHidden?: boolean;
  /**
   * `compact` inks the hole figure a step down, at the size the Customer Population & Activity rings
   * are drawn. Opt-in per ring rather than a token change: every other ring on the page keeps the
   * section-title step, so moving the token would resize all of them.
   */
  centerScale?: DonutCenterScale;
  size?: DonutChartSize;
  /** Formats the value shown on hover. */
  formatValue?: (value: number) => string;
  className?: string;
}

export type DonutCenterScale = 'default' | 'compact';

const DEFAULT_SIZE: DonutChartSize = 94;

/** Screen geometry: outer d=94 r=47 with a d=68 r=34 hole, so a 13px ring. */
const INNER_RADIUS_RATIO = 34 / 47;

/**
 * The screens draw each ring as a conic gradient with hard colour stops, so neighbouring segments
 * meet with no gap and no rounded end. recharts adds both by default, and both read as artefacts
 * the design does not have.
 */
const SEGMENT_PADDING_ANGLE = 0;
const SEGMENT_CORNER_RADIUS = 0;

const HIGHLIGHT_CLASS = 'donut-chart-segment-highlighted';
const REST_OPACITY = 0.5;

/* PLA-8844: `fluid` is the responsive branch and is reached only when the scale context says so,
   i.e. only with the intelligence_responsive_layout flag on. The default branch is byte-identical to
   what this drew before: an inline-flex box shrink-wrapped to a fixed ring. The fluid branch has to
   be a block that fills its column, because an inline-flex box is sized BY its child, so measuring
   it would read the ring back and the diameter could never follow the column. */
const ChartWrapper = styled.div<{ $fluid: boolean; $size: number }>`
  ${({ $fluid, $size }) =>
    $fluid
      ? `display: block; width: 100%; max-width: ${scaledPx($size)};`
      : 'display: inline-flex;'}

  /* box-shadow does not paint on SVG geometry, and drop-shadow() takes the same offset/blur/colour
     triple the elevation token already carries. */
  .${HIGHLIGHT_CLASS} {
    filter: drop-shadow(${tokens.elevation.cta});
  }
`;

const TOOLTIP_CONTENT_STYLE: React.CSSProperties = {
  padding: `${tokens.space['4']} ${tokens.space['8']}`,
  background: tokens.color['surface-card'],
  border: `1px solid ${tokens.color['border-subtle']}`,
  borderRadius: tokens.radius.sm,
  fontSize: tokens.type['label-size'],
  lineHeight: tokens.type['label-line']
};

const TOOLTIP_LABEL_STYLE: React.CSSProperties = { color: tokens.color['text-muted'] };

const TOOLTIP_VALUE_STYLE: React.CSSProperties = { color: tokens.color['text-primary'] };

const CENTER_VALUE_SIZES: Record<DonutCenterScale, string> = {
  default: tokens.type['section-title-size'],
  // A literal rather than a token, so it carries the scale itself.
  compact: scaledPx(16)
};

const CenterValueText = styled.text<{ $scale: DonutCenterScale }>`
  fill: ${tokens.color['text-primary']};
  font-size: ${({ $scale }) => CENTER_VALUE_SIZES[$scale]};
  font-weight: ${tokens.type['section-title-weight']};
`;

const CenterCaptionText = styled.text`
  fill: ${tokens.color['text-muted']};
  font-size: ${tokens.type['label-size']};
  font-weight: ${tokens.type['body-weight']};
`;

interface ICenterLabelProps {
  viewBox?: { cx?: number; cy?: number };
  value: string;
  caption?: string;
  holeRadius: number;
  scale: DonutCenterScale;
}

const CenterLabel: React.FC<ICenterLabelProps> = ({
  viewBox,
  value,
  caption,
  holeRadius,
  scale
}) => {
  const cx = viewBox?.cx ?? 0;
  const cy = viewBox?.cy ?? 0;

  return (
    <>
      {/* The screens fill the hole rather than punching through to the card, so the ring reads as a
          disc with a darker centre. Drawn here because a Label's content is the only child recharts
          renders inside the pie's own layer. */}
      <circle
        cx={cx}
        cy={cy}
        r={holeRadius}
        fill={tokens.color['surface-subpanel']}
        data-testid="donut-chart-center-disc"
      />
      <CenterValueText
        $scale={scale}
        x={cx}
        y={caption ? cy - 6 : cy}
        textAnchor="middle"
        dominantBaseline="central"
        data-testid="donut-chart-center-value"
      >
        {value}
      </CenterValueText>
      {caption ? (
        <CenterCaptionText
          x={cx}
          y={cy + 20}
          textAnchor="middle"
          dominantBaseline="central"
          data-testid="donut-chart-center-label"
        >
          {caption}
        </CenterCaptionText>
      ) : null}
    </>
  );
};

const plottableValue = (value: number): number => (Number.isFinite(value) && value > 0 ? value : 0);

interface IDonutChartViewProps extends IDonutChartProps {
  /** The diameter actually drawn: the fixed `size`, or the measured column width in the fluid path. */
  diameter: number;
  fluid: boolean;
  wrapperRef?: React.RefObject<HTMLDivElement>;
}

const DonutChartView: React.FC<IDonutChartViewProps> = ({
  segments,
  centerValue,
  centerLabel,
  centerLabelHidden = false,
  centerScale = 'default',
  size = DEFAULT_SIZE,
  formatValue = formatNumber,
  className,
  diameter,
  fluid,
  wrapperRef
}) => {
  const [hoveredLabel, setHoveredLabel] = useState<string | null>(null);

  const plotted = (segments ?? []).map(segment => ({
    ...segment,
    value: plottableValue(segment.value)
  }));
  const total = plotted.reduce((sum, segment) => sum + segment.value, 0);

  // Nothing measured means no ring can be drawn, and a bare centre stat floating in an empty box
  // reads as a broken card — the card shows its Insufficient Data state instead (FR § Data states).
  if (total <= 0) {
    return null;
  }

  // One diameter drives the box, the geometry and recharts' own width. Splitting them is what broke
  // pointer mapping the first time a chart in this module was made fluid by CSS alone: recharts
  // resolves hover and tooltip position against its own internal width, so a surface stretched past
  // that width reports the wrong reading (`useMeasuredWidth`).
  const outerRadius = diameter / 2;
  const innerRadius = Math.round(outerRadius * INNER_RADIUS_RATIO);

  // A custom renderer rather than `formatter`: the default per-item row prefixes the reading with
  // the series `name`, which recharts reads from `dataKey` ("value") here — noise beside a name the
  // renderer already prints as its own line.
  const renderTooltip = ({ active, payload }: { active?: boolean; payload?: any[] }) => {
    if (!active || !payload || !payload.length) {
      return null;
    }

    const segment = payload[0].payload as IDonutSegment;

    return (
      <div style={TOOLTIP_CONTENT_STYLE} data-testid="donut-chart-tooltip">
        <div style={TOOLTIP_LABEL_STYLE}>{segment.label}</div>
        <div style={TOOLTIP_VALUE_STYLE}>{formatValue(segment.value)}</div>
      </div>
    );
  };

  return (
    <ChartWrapper
      ref={wrapperRef}
      $fluid={fluid}
      $size={size}
      className={className}
      role="img"
      aria-label={centerLabel ? `${centerValue} ${centerLabel}` : centerValue}
      data-testid="donut-chart"
    >
      <PieChart width={diameter} height={diameter}>
        <Tooltip cursor={false} content={renderTooltip} isAnimationActive={false} />
        <Pie
          data={plotted}
          dataKey="value"
          nameKey="label"
          cx="50%"
          cy="50%"
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          paddingAngle={SEGMENT_PADDING_ANGLE}
          cornerRadius={SEGMENT_CORNER_RADIUS}
          isAnimationActive={false}
          startAngle={90}
          endAngle={-270}
          onMouseEnter={(unused, index: number) => setHoveredLabel(plotted[index]?.label ?? null)}
          onMouseLeave={() => setHoveredLabel(null)}
        >
          {plotted.map(segment => {
            const isHovered = segment.label === hoveredLabel;

            return (
              // recharts ships no categorical palette (every sector defaults to grey) and separates
              // sectors with a white stroke, which reads as cracks on the dark card.
              <Cell
                key={segment.label}
                fill={segment.color}
                stroke="none"
                opacity={hoveredLabel !== null && !isHovered ? REST_OPACITY : 1}
                className={isHovered ? HIGHLIGHT_CLASS : undefined}
              />
            );
          })}
          <Label
            position="center"
            content={
              <CenterLabel
                value={centerValue}
                caption={centerLabelHidden ? undefined : centerLabel}
                holeRadius={innerRadius}
                scale={centerScale}
              />
            }
          />
        </Pie>
      </PieChart>
    </ChartWrapper>
  );
};

/**
 * The responsive branch. Separate from the component below rather than a conditional inside it,
 * because a hook cannot be called conditionally - and mounting the observer only here is what lets
 * the flag-off path stay exactly as it was, with no ResizeObserver on any of the sixteen rings.
 *
 * `useMeasuredWidth` returns 0 until it has a real measurement, and always in jsdom, so the drawn
 * `size` stands in until the column is known.
 */
const FluidDonutChart: React.FC<IDonutChartProps> = props => {
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const measured = useMeasuredWidth(wrapperRef);
  const size = props.size || DEFAULT_SIZE;
  // Never wider than the drawn size. The CSS cap says the same thing, but this diameter is also
  // recharts' own width and must not depend on a style rule having applied.
  const diameter = measured > 0 ? Math.min(measured, size) : size;

  return <DonutChartView {...props} fluid wrapperRef={wrapperRef} diameter={diameter} />;
};

const DonutChart: React.FC<IDonutChartProps> = props => {
  const { responsive } = useIntelligenceScaleContext();

  if (responsive) {
    return <FluidDonutChart {...props} />;
  }

  return <DonutChartView {...props} fluid={false} diameter={props.size || DEFAULT_SIZE} />;
};

export default DonutChart;
